import React, { memo, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  // Person as PersonIcon,
  Chat as ChatIcon,
  Token as TokenIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
  ListAlt as ListAltIcon,
  Psychology as PsychologyIcon,
  History as HistoryIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../contexts/TranslationContext';
import { useGlobalTour } from '../../contexts/TourContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslatedSteps } from '../stepsTour';
import { dashboard, marketplace, rag, chatbox, getSubscriptionInfo, type DashboardData, type ConversationByChannel, type DocStatusResponse} from '../../services/api';
import { enqueueSnackbar } from 'notistack';
import { TourProvider, useTour } from '@reactour/tour';
import VideoProvider from '../../components/VideoProvider';
import { videoUrls, videoTitles } from '../../utils/videoUrls';
import PersonIcon from '@mui/icons-material/Person';
import { formatPersianNumber, toPersianDigits } from '../../utils/validation';
import { parseMarkdown, handleMarkdownButtonClick } from '../../utils/parseMarkdown';
import './chatbot.css';

const DASHBOARD_FALLBACK_CHATBOX_ID = 'L2QJD8YCCN';

const SX_SETTINGS_ICON_BTN = {
  color: 'primary.main',
  '&:hover': { backgroundColor: 'primary.light' },
} as const;

const SX_DASHBOARD_PAGE = { position: 'relative' as const, p: 3, pt:0 , marginTop: {md:'15px' , xs:0}};
const SX_TITLE_MB = { mb: 2 } as const;
const SX_CHAT_PAGE = { px: 3, marginTop: { xs: '3px', md: '8px' } } as const;

const SX_CHAT_LAYOUT = {
  marginTop: { xs: 1, md: 4 },
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: { xs: 4, md: 0 },
  alignItems: { xs: 'center', md: 'flex-start' },
  justifyContent: { xs: 'center', md: 'space-around' },
  width: '100%',
} as const;

const SX_CHAT_INFO = {
  flex: { xs: '0 0 auto', md: '0 1 auto' },
  maxWidth: { xs: '100%', md: '48%' },
  minWidth: 0,
  width: { xs: '100%', md: 'auto' },
} as const;

const SX_CHAT_WIDGET = {
  flex: { xs: '0 0 auto', md: '0 1 auto' },
  maxWidth: { xs: '100%', md: '48%' },
  minWidth: 0,
  width: { xs: '100%', md: 'auto' },
} as const;

const SX_WELCOME_ICON = { color: 'black', width: '24px', height: '24px' } as const;

const SX_WELCOME_ICON_INLINE = {
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  mx: 0.5,
} as const;

const DASHBOARD_WELCOME_ICON_MAP: Record<string, React.ReactNode> = {
  '[جایگاه آیکون دانش]': <ListAltIcon sx={SX_WELCOME_ICON} />,
  '[جایگاه آیکون تنظیمات]': <PsychologyIcon sx={SX_WELCOME_ICON} />,
  '[جایگاه آیکون گفتگو]': <HistoryIcon sx={SX_WELCOME_ICON} />,
  '[جایگاه آیکون پروفایل]': (
    <img src="/images/profile.svg" alt="" style={{ width: '24px', height: '24px' }} />
  ),
  '[جایگاه آیکون چت‌باکس]': <ChatBubbleOutlineIcon sx={SX_WELCOME_ICON} />,
  '[icon:knowledge]': <ListAltIcon sx={SX_WELCOME_ICON} />,
  '[icon:settings]': <PsychologyIcon sx={SX_WELCOME_ICON} />,
  '[icon:conversations]': <HistoryIcon sx={SX_WELCOME_ICON} />,
  '[icon:profile]': (
    <img src="/images/profile.svg" alt="" style={{ width: '24px', height: '24px' }} />
  ),
  '[icon:chatbox]': <ChatBubbleOutlineIcon sx={SX_WELCOME_ICON} />,
};

const FORMATTED_WELCOME_PATTERN =
  /(\{bold\}([\s\S]*?)\{bold\})|(\[(?:جایگاه آیکون[^\]]+|icon:[^\]]+)\])/g;

const parseFormattedWelcomeText = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  FORMATTED_WELCOME_PATTERN.lastIndex = 0;
  while ((match = FORMATTED_WELCOME_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(toPersianDigits(text.slice(lastIndex, match.index)));
    }
    if (match[1]) {
      nodes.push(
        <Box component="span" key={key++} sx={{ fontWeight: 700 }}>
          {toPersianDigits(match[2])}
        </Box>,
      );
    } else if (match[3]) {
      const icon = DASHBOARD_WELCOME_ICON_MAP[match[3]];
      nodes.push(
        icon ? (
          <Box component="span" key={key++} sx={SX_WELCOME_ICON_INLINE}>
            {icon}
          </Box>
        ) : (
          match[3]
        ),
      );
    }
    lastIndex = FORMATTED_WELCOME_PATTERN.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(toPersianDigits(text.slice(lastIndex)));
  }

  return nodes;
};

const renderWelcomeParagraph = (paragraph: string): React.ReactNode =>
  paragraph.split('\n').map((line, lineIndex, lines) => (
    <React.Fragment key={lineIndex}>
      {parseFormattedWelcomeText(line)}
      {lineIndex < lines.length - 1 && <br />}
    </React.Fragment>
  ));

const SX_CHANNEL_ROW = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  p: 1,
  border: '1px solid #E0E0E0',
  borderRadius: '6px',
} as const;

const SX_CHANNEL_ROW_LEFT = { display: 'flex', alignItems: 'center', gap: 1 } as const;

const SX_CHANNEL_LOGO_WRAP_BASE = {
  width: '28px',
  height: '28px',
  padding: '4px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

const SX_CHANNEL_LOGO_IMG = { width: '16px', height: '16px' } as const;

const SX_KNOWLEDGE_ROW_MIDDLE = {
  display: 'grid',
  gridTemplateColumns: '80px 1fr',
  p: 0.75,
  borderBottom: '1px solid #E0E0E0',
  '&:hover': { backgroundColor: '#F9F9F9' },
} as const;

const SX_KNOWLEDGE_ROW_LAST = {
  display: 'grid',
  gridTemplateColumns: '80px 1fr',
  p: 0.75,
  borderBottom: 'none',
  '&:hover': { backgroundColor: '#F9F9F9' },
} as const;

const SX_KNOWLEDGE_ID_CELL = {
  fontWeight: 'bold',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const SX_KNOWLEDGE_CONTENT_CELL = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const SX_PAYMENT_ROW_MIDDLE = {
  display: 'grid',
  gridTemplateColumns: '90px 1fr 80px',
  p: 0.75,
  gap: 0.5,
  borderBottom: '1px solid #E0E0E0',
  '&:hover': { backgroundColor: '#F9F9F9' },
} as const;

const SX_PAYMENT_ROW_LAST = {
  display: 'grid',
  gridTemplateColumns: '90px 1fr 80px',
  p: 0.75,
  gap: 0.5,
  borderBottom: 'none',
  '&:hover': { backgroundColor: '#F9F9F9' },
} as const;

const SX_PAYMENT_REF_CELL = { fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis' } as const;
const SX_PAYMENT_PLAN_CELL = { fontWeight: 'bold', fontSize: '0.7rem' } as const;
const SX_PAYMENT_AMOUNT_CELL = {
  fontWeight: 'bold',
  fontSize: '0.7rem',
  textAlign: 'right',
} as const;

const SX_INTEGRATION_COLUMN = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 0.5,
} as const;

const SX_INTEGRATION_AVATAR = {
  position: 'relative',
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  overflow: 'hidden',
  border: '3px solid',
  borderColor: 'green',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'white',
  '&:hover': {
    transform: 'scale(1.1)',
    transition: 'transform 0.2s',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
} as const;

const SX_INTEGRATION_IMG = { width: '90%', height: '90%', objectFit: 'contain' } as const;

const SX_INTEGRATION_FALLBACK = {
  width: '100%',
  height: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'white',
  color: 'primary.main',
  fontSize: '1.5rem',
  fontWeight: 'bold',
} as const;

const SX_INTEGRATION_FALLBACK_VISIBLE = { ...SX_INTEGRATION_FALLBACK, display: 'flex' } as const;
const SX_INTEGRATION_FALLBACK_HIDDEN = { ...SX_INTEGRATION_FALLBACK, display: 'none' } as const;

const SX_INTEGRATION_CAPTION = {
  fontSize: '0.65rem',
  textAlign: 'center',
  maxWidth: '80px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  mt: 0.5,
} as const;

type ChannelStatusRowProps = {
  label: string;
  logo: string;
  bgColor: string;
  chipLabel: string;
  chipColor: 'success' | 'error';
};

const DashboardChannelStatusRow = memo(function DashboardChannelStatusRow({
  label,
  logo,
  bgColor,
  chipLabel,
  chipColor,
}: ChannelStatusRowProps) {
  const logoWrapSx = useMemo(
    () => ({
      ...SX_CHANNEL_LOGO_WRAP_BASE,
      backgroundColor: bgColor,
    }),
    [bgColor],
  );

  return (
    <Box sx={SX_CHANNEL_ROW}>
      <Box sx={SX_CHANNEL_ROW_LEFT}>
        <Box sx={logoWrapSx}>
          <Box component="img" src={logo} alt={label} sx={SX_CHANNEL_LOGO_IMG} />
        </Box>
        <Typography variant="body2">{label}</Typography>
      </Box>
      <Chip label={chipLabel} color={chipColor} variant="filled" size="small" />
    </Box>
  );
});

type KnowledgeTableRowProps = {
  rowId: string;
  contentSummary: string;
  isLast: boolean;
};

const DashboardKnowledgeTableRow = memo(function DashboardKnowledgeTableRow({
  rowId,
  contentSummary,
  isLast,
}: KnowledgeTableRowProps) {
  const rowSx = isLast ? SX_KNOWLEDGE_ROW_LAST : SX_KNOWLEDGE_ROW_MIDDLE;
  return (
    <Box sx={rowSx}>
      <Typography variant="caption" sx={SX_KNOWLEDGE_ID_CELL}>
        {toPersianDigits(rowId)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={SX_KNOWLEDGE_CONTENT_CELL}>
        {contentSummary ? toPersianDigits(contentSummary) : '-'}
      </Typography>
    </Box>
  );
});

type PaymentTableRowProps = {
  refId: string | null;
  planLabel: string;
  amountFormatted: string;
  isLast: boolean;
};

const DashboardPaymentTableRow = memo(function DashboardPaymentTableRow({
  refId,
  planLabel,
  amountFormatted,
  isLast,
}: PaymentTableRowProps) {
  const rowSx = isLast ? SX_PAYMENT_ROW_LAST : SX_PAYMENT_ROW_MIDDLE;
  return (
    <Box sx={rowSx}>
      <Typography variant="caption" sx={SX_PAYMENT_REF_CELL}>
        {refId ? toPersianDigits(refId) : '-'}
      </Typography>
      <Typography variant="caption" sx={SX_PAYMENT_PLAN_CELL}>
        {planLabel}
      </Typography>
      <Typography variant="caption" color="primary" sx={SX_PAYMENT_AMOUNT_CELL}>
        {amountFormatted}
      </Typography>
    </Box>
  );
});

type IntegrationTileProps = {
  imageUrl?: string | null;
  title: string;
  missingImageTitle: string;
};

const DashboardIntegrationTile = memo(function DashboardIntegrationTile({
  imageUrl,
  title,
  missingImageTitle,
}: IntegrationTileProps) {
  const handleImgError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget;
    el.style.display = 'none';
    const sibling = el.nextElementSibling as HTMLElement | null;
    if (sibling) {
      sibling.style.display = 'flex';
    }
  }, []);

  const fallbackSx = imageUrl ? SX_INTEGRATION_FALLBACK_HIDDEN : SX_INTEGRATION_FALLBACK_VISIBLE;

  return (
    <Box sx={SX_INTEGRATION_COLUMN}>
      <Box sx={SX_INTEGRATION_AVATAR} title={title}>
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={title}
            sx={SX_INTEGRATION_IMG}
            onError={handleImgError}
          />
        ) : null}
        <Box sx={fallbackSx}>{title?.charAt(0).toUpperCase() || '?'}</Box>
      </Box>
      <Typography variant="caption" sx={SX_INTEGRATION_CAPTION}>
        {title || missingImageTitle}
      </Typography>
    </Box>
  );
});

interface KnowledgeEntry {
  id: string;
  content_summary: string;
  created_at: string;
}

interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

function getAllChannelsWithData(channelData: ConversationByChannel[]) {
  const allPossibleChannels = ['instagram', 'telegram', 'website_chatbox', 'bale'];

  return allPossibleChannels.map((channelName) => {
    const existingData = channelData.find((conv) => conv.channel_name === channelName);
    return {
      channel_name: channelName,
      message_count: existingData ? existingData.message_count : 0,
    };
  });
}

function getFirst30Words(text: string) {
  const words = text.split(' ');
  const excerpt = words.length <= 80 ? text : `${words.slice(0, 80).join(' ')}...`;
  return toPersianDigits(excerpt);
}

const DashboardContent: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [knowledgeList, setKnowledgeList] = useState<KnowledgeEntry[]>([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(true);
  const [knowledgeCount, setKnowledgeCount] = useState<number>(0);
  const [activeIntegrations, setActiveIntegrations] = useState<any[]>([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [subscriptionPlan, setSubscriptionPlan] = useState<'intern' | 'expert' | 'master' | null>(null);
  const [openSystemPromptModal, setOpenSystemPromptModal] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const chatboxRef = useRef<HTMLUListElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { t, tArray, currentLanguage } = useTranslation();
  const { user } = useAuth();
  const tRef = useRef(t);
  tRef.current = t;
  const sessionExpiredAnnouncedRef = useRef(false);
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  const navigate = useNavigate();

  // Determine chatbot position based on language direction (RTL = right, LTR = left)
  const positionChatbox = currentLanguage === 'fa' ? 'right' : 'left';

  // Scroll chatbox to bottom
  const scrollToBottom = () => {
    if (chatboxRef.current) {
      chatboxRef.current.scrollTo(0, chatboxRef.current.scrollHeight);
    }
  };

  // Save chat history to localStorage
  const saveChatHistory = (messages: ChatMessage[]) => {
    try {
      localStorage.setItem('dashboardChatHistory', JSON.stringify(messages));
    } catch (error) {
      console.error('Error saving chat history:', error);
    }
  };

  // Clear chat history
  const clearChatHistory = () => {
    try {
      // Remove from localStorage
      localStorage.removeItem('dashboardChatHistory');
      
      // Reset to welcome message only
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        content: t('dashboard.chatbox.howCanIHelpYou'),
        sender: 'ai',
        timestamp: new Date(),
      };
      
      setChatMessages([welcomeMessage]);
      scrollToBottom();
    } catch (error) {
      console.error('Error clearing chat history:', error);
    }
  };

  // Load chat history from localStorage
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem('dashboardChatHistory');
      if (savedHistory) {
        const parsed = JSON.parse(savedHistory);
        // Convert timestamp strings back to Date objects
        const messages = parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp),
        }));
        setChatMessages(messages);
      } else {
        // Initialize with welcome message
        const welcomeMessage: ChatMessage = {
          id: 'welcome',
          content: t('dashboard.chatbox.howCanIHelpYou'),
          sender: 'ai',
          timestamp: new Date(),
        };
        setChatMessages([welcomeMessage]);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      // Initialize with welcome message on error
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        content: t('dashboard.chatbox.howCanIHelpYou'),
        sender: 'ai',
        timestamp: new Date(),
      };
      setChatMessages([welcomeMessage]);
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Handle sending message to chatbox API
  const handleSendMessage = async () => {
    const userMessageText = chatMessage.trim();
    const activeChatboxId = DASHBOARD_FALLBACK_CHATBOX_ID;
    const dashboardThreadId = user?.user?.id ? String(user.user.id) : null;
    if (!userMessageText || sendingMessage || !activeChatboxId || !dashboardThreadId) return;

    // Create user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: userMessageText,
      sender: 'user',
      timestamp: new Date(),
    };

    // Add user message to chat
    const updatedMessages = [...chatMessages, userMessage];
    setChatMessages(updatedMessages);
    saveChatHistory(updatedMessages);
    setChatMessage('');
    
    // Reset textarea height
    setTimeout(() => {
      scrollToBottom();
    }, 100);

    // Show typing indicator after 600ms
    setTimeout(() => {
      const typingMessage: ChatMessage = {
        id: `typing-${Date.now()}`,
        content: t('dashboard.chatbox.isTyping'),
        sender: 'ai',
        timestamp: new Date(),
      };
      setChatMessages((prev) => [...prev, typingMessage]);
      scrollToBottom();
      
      // Send message to API
      handleBotResponse(typingMessage.id, userMessageText, activeChatboxId, dashboardThreadId);
    }, 600);
  };

  // Poll for chatbox result
  const pollChatboxResult = async (responseId: string, typingMessageId: string) => {
    const poll = async () => {
      try {
        const { data } = await chatbox.getResult(responseId);
        
        // بررسی فیلد status
        if (data.status === 'pending') {
          // هنوز جواب آماده نیست، polling را ادامه بده
          return false;
        }

        // اگر status pending نبود، یعنی جواب آماده است
        // Clear polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Remove typing message and add bot response
        setChatMessages((prev) => {
          const filtered = prev.filter((msg) => msg.id !== typingMessageId);
          const botMessage: ChatMessage = {
            id: Date.now().toString(),
            content: data.response || data.message || 'پاسخ دریافت شد',
            sender: 'ai',
            timestamp: new Date(),
          };
          const updated = [...filtered, botMessage];
          saveChatHistory(updated);
          return updated;
        });

        setSendingMessage(false);
        scrollToBottom();
        return true;
      } catch (error: any) {
        console.error('Error polling result:', error);
        // اگر خطای غیر از 404/202 باشد، polling را متوقف کن
        if (error.message !== 'Failed to get result') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // Remove typing message and show error
          setChatMessages((prev) => {
            const filtered = prev.filter((msg) => msg.id !== typingMessageId);
            const errorMessage: ChatMessage = {
              id: Date.now().toString(),
              content: t('dashboard.chatbox.errorMessage'),
              sender: 'ai',
              timestamp: new Date(),
            };
            const updated = [...filtered, errorMessage];
            saveChatHistory(updated);
            return updated;
          });

          setSendingMessage(false);
          scrollToBottom();
          return true;
        }
        return false;
      }
    };

    // Start polling immediately
    const gotResult = await poll();
    if (gotResult) {
      return;
    }

    // If not ready, start polling every 1 second
    pollingIntervalRef.current = setInterval(async () => {
      const gotResult = await poll();
      if (gotResult && pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }, 5000);
  };

  // Handle bot response
  const handleBotResponse = async (
    typingMessageId: string,
    userMessageText: string,
    activeChatboxId: string,
    dashboardThreadId: string,
  ) => {
    setSendingMessage(true);
    try {
      const { data } = await chatbox.sendMessage(activeChatboxId, {
        message: userMessageText,
        thread_id: dashboardThreadId,
        username: dashboardThreadId,
      });
      const responseId = data.correlation_id;
      
      if (!responseId) {
        throw new Error('No correlation_id received');
      }

      // Start polling for result
      await pollChatboxResult(responseId, typingMessageId);
    } catch (error: any) {
      console.error('Error sending message:', error);
      
      // Clear polling if exists
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      // Remove typing message and show error
      setChatMessages((prev) => {
        const filtered = prev.filter((msg) => msg.id !== typingMessageId);
        const errorMessage: ChatMessage = {
          id: Date.now().toString(),
          content: t('dashboard.chatbox.errorMessage'),
          sender: 'ai',
          timestamp: new Date(),
        };
        const updated = [...filtered, errorMessage];
        saveChatHistory(updated);
        return updated;
      });

      setSendingMessage(false);
      scrollToBottom();
    }
  };

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await dashboard.getDashboardData();
        setData(response.data);
      } catch (error: any) {
        if (error.response?.status === 401) {
          if (!sessionExpiredAnnouncedRef.current) {
            enqueueSnackbar(tRef.current('dashboard.error.sessionExpired'), { 
              variant: 'error',
              autoHideDuration: 5000,
              style: {
                padding: '20px',
                marginTop: '20px',
              },
            });
            sessionExpiredAnnouncedRef.current = true;
          }
          setTimeout(() => {
            localStorage.removeItem('token');
            window.location.href = '/auth/auth';
          }, 4000);
        }
      } finally {
        setLoading(false);
      }
    };

    const fetchKnowledgeList = async () => {
      try {
        const response = await rag.getDocuments();
        const statuses = response.data.statuses || {};
        
        // جمع‌آوری تمام اسناد از تمام statuses
        const allDocuments: DocStatusResponse[] = [];
        Object.values(statuses).forEach((docs) => {
          allDocuments.push(...docs);
        });
        
        // محاسبه تعداد کل
        setKnowledgeCount(allDocuments.length);
        
        // مرتب‌سازی بر اساس created_at (جدیدترین اول)
        const sortedDocuments = allDocuments.sort((a, b) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return dateB - dateA;
        });
        
        // تبدیل به فرمت KnowledgeEntry و گرفتن 3 مورد اول
        const knowledgeEntries: KnowledgeEntry[] = sortedDocuments.slice(0, 3).map((doc) => ({
          id: doc.id,
          content_summary: doc.content_summary,
          created_at: doc.created_at,
        }));
        
        setKnowledgeList(knowledgeEntries);
      } catch (error: any) {
        console.error('Error fetching knowledge list:', error);
        setKnowledgeCount(0);
      } finally {
        setKnowledgeLoading(false);
      }
    };

    const fetchActiveIntegrations = async () => {
      try {
        const response = await marketplace.getUserIntegrations({ is_active: true });
        setActiveIntegrations(response.data.items || []);
      } catch (error: any) {
        console.error('Error fetching active integrations:', error);
      } finally {
        setIntegrationsLoading(false);
      }
    };

    fetchDashboard();
    fetchKnowledgeList();
    fetchActiveIntegrations();
  }, []);

  useEffect(() => {
    const fetchSubscriptionPlan = async () => {
      try {
        const response = await getSubscriptionInfo();
        if (response.has_active_subscription && response.data) {
          setSubscriptionPlan(response.data.plan);
        } else {
          setSubscriptionPlan(null);
        }
      } catch (error) {
        console.error('Failed to fetch subscription info:', error);
        setSubscriptionPlan(null);
      }
    };

    fetchSubscriptionPlan();
  }, []);

  // Register dashboard tour function
  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  const channelsStatus = useMemo(() => {
    if (!data) return [];
    return [
      {
        name: 'instagram',
        label: t('dashboard.activeChannels.instagram'),
        enabled: data.onboarding.instagram_enabled,
        logo: '/images/instagram-2016-logo-svgrepo-com.svg',
        bgColor: '#F8BBD0',
      },
      {
        name: 'telegram',
        label: t('dashboard.activeChannels.telegram'),
        enabled: data.onboarding.telegram_enabled,
        logo: '/images/telegram-logo-svgrepo-com.svg',
        bgColor: '#BBDEFB',
      },
      {
        name: 'website',
        label: t('dashboard.activeChannels.website'),
        enabled: data.onboarding.website_chatbox_enabled,
        logo: '/images/website-click-svgrepo-com.svg',
        bgColor: '#FFCCBC',
      },
      {
        name: 'bale',
        label: t('dashboard.activeChannels.bale'),
        enabled: data.onboarding.bale_enabled,
        logo: '/images/bale-svgrepo-com.svg',
        bgColor: '#DCEDC8',
      },
    ];
  }, [data, t]);

  const getChannelInfo = useCallback(
    (channelName: string) => {
      const channelMap: { [key: string]: { logo: string; bgColor: string; label: string } } = {
        instagram: {
          logo: '/images/instagram-2016-logo-svgrepo-com.svg',
          bgColor: '#F8BBD0',
          label: t('dashboard.activeChannels.instagram'),
        },
        telegram: {
          logo: '/images/telegram-logo-svgrepo-com.svg',
          bgColor: '#BBDEFB',
          label: t('dashboard.activeChannels.telegram'),
        },
        bale: {
          logo: '/images/bale-svgrepo-com.svg',
          bgColor: '#DCEDC8',
          label: t('dashboard.activeChannels.bale'),
        },
        website_chatbox: {
          logo: '/images/website-click-svgrepo-com.svg',
          bgColor: '#FFCCBC',
          label: t('dashboard.activeChannels.website'),
        },
        instagram_comment: {
          logo: '/images/instagram-2016-logo-svgrepo-com.svg',
          bgColor: '#F8BBD0',
          label: t('dashboard.activeChannels.instagram_comment'),
        },
        unknown: {
          logo: '/images/chatbox.svg',
          bgColor: '#E0E0E0',
          label: channelName,
        },
      };

      return channelMap[channelName] || channelMap.unknown;
    },
    [t],
  );

  const getPlanLabel = useCallback(
    (planName: string) => {
      const planMap: { [key: string]: string } = {
        master: t('onboarding.subscription.master'),
        expert: t('onboarding.subscription.expert'),
        intern: t('onboarding.subscription.intern'),
      };
      return planMap[planName.toLowerCase()] || planName;
    },
    [t],
  );

  const totalMessages = useMemo(
    () =>
      data ? data.conversations_by_channel.reduce((sum, conv) => sum + conv.message_count, 0) : 0,
    [data],
  );

  const totalMessages7Days = useMemo(
    () =>
      data
        ? data.conversations_last_7_days_by_channel.reduce((sum, conv) => sum + conv.message_count, 0)
        : 0,
    [data],
  );

  const displayedIntegrations = useMemo(
    () => activeIntegrations.slice(0, 6),
    [activeIntegrations],
  );

  const recentPaymentsPreview = useMemo(
    () => (data?.recent_payments ?? []).slice(0, 3),
    [data],
  );

  const channelChipLabels = useMemo(
    () => ({ active: t('channel.active'), inactive: t('channel.inactive') }),
    [t],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          {t('dashboard.error.fetchError')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
    {totalMessages !== 0 ? (
    <Box sx={SX_DASHBOARD_PAGE}>
      <Typography variant="h5" gutterBottom className="dashboard-title" sx={SX_TITLE_MB}>
        {t('menu.dashboard')}
      </Typography>

      {/* System Prompt and User Info Row */}
      {
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* System Prompt - دو سوم */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ height: '100%', border: '1px solid #E0E0E0', borderRadius: '10px', p: 2 , boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, borderBottom: '1px solid #E0E0E0',pb:1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t('dashboard.systemPrompt.title')}
                </Typography>
                {(subscriptionPlan === 'expert' || subscriptionPlan === 'master') && (
                  <IconButton
                    size="small"
                    onClick={() => navigate('/aida-settings?tab=1')}
                    sx={SX_SETTINGS_ICON_BTN}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {data.system_prompt && data.system_prompt.length > 0 ? (
                <Box sx={{ p: 2 }}>
                  {data.system_prompt.length === 1 ? (
                    <>
                      <Typography 
                        variant="body1" 
                        className="dashboard-subtitle"
                        sx={{ textAlign: 'justify', mb: 1 }}
                      >
                        {getFirst30Words(data.system_prompt[0])}
                      </Typography>
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button
                          variant="outlined"
                          onClick={() => setOpenSystemPromptModal(true)}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('dashboard.systemPrompt.showMore')}
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      {(data.system_prompt.length > 3 ? data.system_prompt.slice(0, 3) : data.system_prompt).map((prompt, index) => (
                        <Typography 
                          key={index}
                          variant="body1" 
                          className="dashboard-subtitle"
                          sx={{ textAlign: 'justify', mb: 1 }}
                        >
                          {toPersianDigits(prompt)}
                        </Typography>
                      ))}
                      {data.system_prompt.length > 3 && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                          <Button
                            variant="outlined"
                            onClick={() => setOpenSystemPromptModal(true)}
                            sx={{ textTransform: 'none' }}
                          >
                            {t('dashboard.systemPrompt.showMore')}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              ) : (
                <Typography 
                  variant="body1" 
                  className="dashboard-subtitle"
                  sx={{ textAlign: 'justify', p: 2 }}
                >
                  {t('dashboard.systemPrompt.noSystemPrompt')}
                </Typography>
              )}
            </Paper>
          </Grid>
          

          {/* اطلاعات شخصی */}
          {
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2, height: '100%',border: '1px solid #E0E0E0', borderRadius: '20px' , boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' }} className="dashboard-user-info">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <PersonIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {t('dashboard.userInfo.title')}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => navigate('/profile-and-business')}
                    sx={SX_SETTINGS_ICON_BTN}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.userInfo.fullName')}
                    </Typography>
                    <Typography variant="body2">
                      {data.user.full_name || t('dashboard.userInfo.notSet')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.userInfo.email')}
                    </Typography>
                    <Typography variant="body2">
                      {data.user.email || t('dashboard.userInfo.notSet')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.userInfo.phoneNumber')}
                    </Typography>
                    <Typography variant="body2">
                      {data.user.phone_number ? toPersianDigits(data.user.phone_number) : ''}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          }
        </Grid>
      }

      {/* System Prompt Modal */}
      <Dialog
        open={openSystemPromptModal}
        onClose={() => setOpenSystemPromptModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('dashboard.systemPrompt.title')}
        </DialogTitle>
        <DialogContent>
          {data?.system_prompt && data.system_prompt.length > 0 && (
            <Box sx={{ pt: 2 }}>
              {data.system_prompt.map((prompt, index) => (
                <Typography 
                  key={index}
                  variant="body1" 
                  className="dashboard-subtitle"
                  sx={{ textAlign: 'justify', mb: index < data.system_prompt!.length - 1 ? 2 : 0 }}
                >
                  {toPersianDigits(prompt)}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSystemPromptModal(false)}>
            {t('dashboard.systemPrompt.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        

        {/* کانال‌های فعال */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper sx={{ p: 2, height: '100%',border: '1px solid #E0E0E0', borderRadius: '20px' , boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' }}  className="dashboard-active-channels">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {t('dashboard.activeChannels.title')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => navigate('/aida-settings?tab=5')}
                sx={SX_SETTINGS_ICON_BTN}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {channelsStatus.map((channel) => (
                <DashboardChannelStatusRow
                  key={channel.name}
                  label={channel.label}
                  logo={channel.logo}
                  bgColor={channel.bgColor}
                  chipLabel={channel.enabled ? channelChipLabels.active : channelChipLabels.inactive}
                  chipColor={channel.enabled ? 'success' : 'error'}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* تعداد دانش */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper sx={{ p: 2, height: '100%',border: '1px solid #E0E0E0', borderRadius: '20px' , boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' }}  className="dashboard-knowledge-count">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{display: 'flex' , alignItems: 'center'}}>
                <SchoolIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {t('dashboard.knowledgeCount.title')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => navigate('/knowledge-base')}
                sx={SX_SETTINGS_ICON_BTN}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ textAlign: 'center', mb: 1.5 }}>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                {formatPersianNumber(knowledgeCount)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('dashboard.knowledgeCount.count')}
              </Typography>
            </Box>
            {knowledgeLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="60px">
                <CircularProgress size={20} />
              </Box>
            ) : knowledgeList.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}>
                  {t('dashboard.knowledgeCount.lasts_knowledge')}:
                </Typography>
                <Box sx={{ border: '1px solid #E0E0E0', borderRadius: '6px', overflow: 'hidden' }}>
                  {/* Table Header */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1fr',
                      backgroundColor: '#F5F5F5',
                      borderBottom: '1px solid #E0E0E0',
                      p: 0.75,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {t('dashboard.knowledgeCount.type')}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {t('dashboard.knowledgeCount.content')}
                    </Typography>
                  </Box>
                  {/* Table Body */}
                  {knowledgeList.map((item, index) => (
                    <DashboardKnowledgeTableRow
                      key={item.id}
                      rowId={item.id}
                      contentSummary={item.content_summary}
                      isLast={index === knowledgeList.length - 1}
                    />
                  ))}
                </Box>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {t('dashboard.knowledgeCount.noKnowledge')}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* مصرف توکن */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper sx={{ p: 2, height: '100%',border: '1px solid #E0E0E0', borderRadius: '20px' , boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' }}  className="dashboard-token-usage">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <TokenIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('dashboard.tokenUsage.title')}
              </Typography>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 , height: '80%', justifyContent: 'center' }}>
              {/* Pie Chart - نمودار دایره‌ای کامل */}
              {(() => {
                const total = data.token_usage.total_tokens;
                const inputTokens = data.token_usage.total_input_tokens;
                const outputTokens = data.token_usage.total_output_tokens;
                
                // اگر هیچ توکنی رد و بدل نشده باشد، پیام نمایش داده شود
                if (total === 0 || (inputTokens === 0 && outputTokens === 0)) {
                  return (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '140px',
                      width: '140px',
                      textAlign: 'center',
                      px: 2
                    }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          fontSize: '0.75rem',
                          lineHeight: 1.4,
                          fontWeight: 500
                        }}
                      >
                        {t('dashboard.tokenUsage.noTokensExchanged')}
                      </Typography>
                    </Box>
                  );
                }
                
                // در غیر این صورت نمودار دایره‌ای نمایش داده شود
                return (
                  <Box sx={{ position: 'relative', width: '140px', height: '140px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <svg width="140" height="140" viewBox="0 0 140 140">
                      {(() => {
                        const inputPercentage = (inputTokens / total) * 100;
                        const outputPercentage = (outputTokens / total) * 100;
                        
                        // محاسبه زوایا برای pie chart
                        const inputAngle = (inputPercentage / 100) * 360;
                        const outputAngle = (outputPercentage / 100) * 360;
                        
                        // تبدیل زاویه به مختصات
                        const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
                          const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
                          return {
                            x: centerX + (radius * Math.cos(angleInRadians)),
                            y: centerY + (radius * Math.sin(angleInRadians))
                          };
                        };
                        
                        const radius = 70;
                        const centerX = 70;
                        const centerY = 70;
                        
                        // مسیر برای input tokens (سبز)
                        const inputEnd = polarToCartesian(centerX, centerY, radius, inputAngle);
                        const inputPath = [
                          `M ${centerX} ${centerY}`,
                          `L ${centerX} ${centerY - radius}`,
                          `A ${radius} ${radius} 0 ${inputAngle > 180 ? 1 : 0} 1 ${inputEnd.x} ${inputEnd.y}`,
                          'Z'
                        ].join(' ');
                        
                        // مسیر برای output tokens (قرمز)
                        const outputStart = inputEnd;
                        const outputPath = [
                          `M ${centerX} ${centerY}`,
                          `L ${outputStart.x} ${outputStart.y}`,
                          `A ${radius} ${radius} 0 ${outputAngle > 180 ? 1 : 0} 1 ${centerX} ${centerY - radius}`,
                          'Z'
                        ].join(' ');
                        
                        return (
                          <>
                            <path d={inputPath} fill="#80CBC4" />
                            <path d={outputPath} fill="#009688" />
                          </>
                        );
                      })()}
                    </svg>
                  </Box>
                );
              })()}
              
              {/* Legend - فقط زمانی نمایش داده شود که توکنی وجود داشته باشد */}
              {(() => {
                const total = data.token_usage.total_tokens;
                const inputTokens = data.token_usage.total_input_tokens;
                const outputTokens = data.token_usage.total_output_tokens;
                
                // اگر هیچ توکنی رد و بدل نشده باشد، legend نمایش داده نشود
                if (total === 0 || (inputTokens === 0 && outputTokens === 0)) {
                  return null;
                }
                
                return (
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#80CBC4' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                          {t('dashboard.tokenUsage.inputTokens')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                          {formatPersianNumber(data.token_usage.total_input_tokens)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#009688' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                          {t('dashboard.tokenUsage.outputTokens')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                          {formatPersianNumber(data.token_usage.total_output_tokens)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          </Paper>
        </Grid>
        
        {/* هیستوری گفتگوها */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%',border: '1px solid #E0E0E0', borderRadius: '20px' , boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' }}  className="dashboard-conversations">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                <ChatIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {t('dashboard.conversations.title')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => navigate('/conversations')}
                sx={SX_SETTINGS_ICON_BTN}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="h5" color="primary">
                {formatPersianNumber(totalMessages)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.conversations.totalMessages')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
              {/* نمودار میله‌ای پیام‌ها */}
              <Box sx={{ width: '100%', maxWidth: '400px' }}>
                {(() => {
                  const allChannelsData = getAllChannelsWithData(data.conversations_by_channel);
                  const maxCount = Math.max(...allChannelsData.map(c => c.message_count), 1); // حداقل 1 برای جلوگیری از تقسیم بر صفر
                  const chartHeight = 160;
                  const barSpacing = 30;
                  const barWidth = Math.min(45, (350 / allChannelsData.length) - barSpacing);
                  const leftMargin = 20;
                  const totalWidth = allChannelsData.length * (barWidth + barSpacing) + leftMargin + 20;

                  return (
                    <svg width="100%" height={chartHeight + 70} viewBox={`0 0 ${totalWidth} ${chartHeight + 70}`}>
                      {allChannelsData.map((conv, index) => {
                        const channelInfo = getChannelInfo(conv.channel_name);
                        const barHeight = conv.message_count === 0
                          ? 1
                          : (conv.message_count / maxCount) * (chartHeight - 20);
                        const x = leftMargin + 10 + index * (barWidth + barSpacing);
                        const y = chartHeight + 10 - barHeight;
                        
                        return (
                          <g key={index}>
                            {/* میله نمودار */}
                            <rect
                              x={x}
                              y={y}
                              width={barWidth}
                              height={barHeight}
                              fill={channelInfo.bgColor}
                              rx="4"
                            />
                            
                            {/* تعداد پیام بالای میله */}
                            <text
                              x={x + barWidth / 2}
                              y={y - 8}
                              textAnchor="middle"
                              fontSize="11"
                              fontWeight="bold"
                              fill="#009688"
                            >
                              {formatPersianNumber(conv.message_count)}
                            </text>
                            
                            {/* لوگوی کانال در پایین */}
                            <image
                              href={channelInfo.logo}
                              x={x + barWidth / 2 - 11}
                              y={chartHeight + 20}
                              width="24"
                              height="24"
                              
                            />
                          </g>
                        );
                      })}
                    </svg>
                  );
                })()}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* گفتگوهای ۷ روز اخیر */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%',border: '1px solid #E0E0E0', borderRadius: '20px' , boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)' }}  className="dashboard-conversations-last-7-days">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                <ChatIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {t('dashboard.conversationsLast7Days.title')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => navigate('/conversations')}
                sx={SX_SETTINGS_ICON_BTN}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="h5" color="primary">
                {formatPersianNumber(totalMessages7Days)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.conversations.totalMessages')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
              {/* نمودار میله‌ای پیام‌های ۷ روز اخیر */}
              <Box sx={{ width: '100%', maxWidth: '400px' }}>
                {(() => {
                  const allChannelsData = getAllChannelsWithData(data.conversations_last_7_days_by_channel);
                  const maxCount = Math.max(...allChannelsData.map(c => c.message_count), 1); // حداقل 1 برای جلوگیری از تقسیم بر صفر
                  const chartHeight = 160;
                  const barSpacing = 30;
                  const barWidth = Math.min(45, (350 / allChannelsData.length) - barSpacing);
                  const leftMargin = 20;
                  const totalWidth = allChannelsData.length * (barWidth + barSpacing) + leftMargin + 20;

                  return (
                    <svg width="100%" height={chartHeight + 70} viewBox={`0 0 ${totalWidth} ${chartHeight + 70}`}>
                      {allChannelsData.map((conv, index) => {
                        const channelInfo = getChannelInfo(conv.channel_name);
                        const barHeight = conv.message_count === 0
                          ? 1
                          : (conv.message_count / maxCount) * (chartHeight - 20);
                        const x = leftMargin + 10 + index * (barWidth + barSpacing);
                        const y = chartHeight + 10 - barHeight;
                        
                        return (
                          <g key={index}>
                            {/* میله نمودار */}
                            <rect
                              x={x}
                              y={y}
                              width={barWidth}
                              height={barHeight}
                              fill={channelInfo.bgColor}
                              rx="4"
                            />
                            
                            {/* تعداد پیام بالای میله */}
                            <text
                              x={x + barWidth / 2}
                              y={y - 8}
                              textAnchor="middle"
                              fontSize="11"
                              fontWeight="bold"
                              fill="#009688"
                            >
                              {formatPersianNumber(conv.message_count)}
                            </text>
                            
                            {/* لوگوی کانال در پایین */}
                            <image
                              href={channelInfo.logo}
                              x={x + barWidth / 2 - 11}
                              y={chartHeight + 20}
                              width="24"
                              height="24"
                              
                            />
                          </g>
                        );
                      })}
                    </svg>
                  );
                })()}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Last row with space-between */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 2, flexWrap: 'wrap' }}>

            {/* active integrations */}
            <Box sx={{ flex: '1', maxWidth: '500px', minWidth: '300px'}}>
              <Paper sx={{ p: 2, height: '100%' ,border: '1px solid #E0E0E0',borderRadius: '20px' , boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.15)' }} className="dashboard-active-integrations">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{display: 'flex', alignItems: 'center'}}>
                <PeopleIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {t('dashboard.activeIntegrations.title')}
              </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => navigate('/aida-settings?tab=3&subtab=1')}
                sx={SX_SETTINGS_ICON_BTN}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            
            {integrationsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
                <CircularProgress size={24} />
              </Box>
            ) : activeIntegrations.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, minHeight: '180px' }}>
                {/* عکس‌های پروفایل دایره‌ای */}
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: 2,
                  maxWidth: '100%',
                }}>
                  {displayedIntegrations.map((integration) => (
                    <DashboardIntegrationTile
                      key={integration.id}
                      imageUrl={integration.integration?.image_url}
                      title={integration.integration?.title ?? ''}
                      missingImageTitle={t('dashboard.activeIntegrations.noActiveIntegrations')}
                    />
                  ))}
                </Box>
                
                
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.activeIntegrations.noActiveIntegrations')}
                </Typography>
              </Box>
            )}
          </Paper>
            </Box>
            

            {/* payment history */}
            <Box sx={{ flex: '1', maxWidth: '500px', minWidth: '300px'}}>
              <Paper sx={{ p: 2, height: '100%',border: '1px solid #E0E0E0', borderRadius: '20px' , boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.15)' }} className="dashboard-recent-payments">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{display: 'flex', alignItems: 'center'}}>
                  <PaymentIcon color="primary" sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {t('dashboard.recentPayments.title')}
                  </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => navigate('/payments')}
                    sx={SX_SETTINGS_ICON_BTN}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                {data.recent_payments.length > 0 ? (
                  <Box sx={{ border: '1px solid #E0E0E0', borderRadius: '6px', overflow: 'hidden' }}>
                    {/* Table Header */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '90px 1fr 80px',
                        backgroundColor: '#F5F5F5',
                        borderBottom: '1px solid #E0E0E0',
                        p: 0.75,
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                        {t('dashboard.recentPayments.refId')}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                        {t('dashboard.recentPayments.plan')}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem', textAlign: 'right' }}>
                        {t('dashboard.recentPayments.amount')}
                      </Typography>
                    </Box>
                    {/* Table Body */}
                    {recentPaymentsPreview.map((payment, index) => (
                      <DashboardPaymentTableRow
                        key={payment.ref_id}
                        refId={payment.ref_id}
                        planLabel={getPlanLabel(payment.plan)}
                        amountFormatted={formatPersianNumber(payment.amount)}
                        isLast={index === recentPaymentsPreview.length - 1}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t('dashboard.recentPayments.noPayments')}
                  </Typography>
                )}
              </Paper>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
    ) : (
      <Box className="show-chatbot" sx={SX_CHAT_PAGE}>
        <Box className="chat-layout_AIDACHATBOX" sx={SX_CHAT_LAYOUT}>
          <Box sx={SX_CHAT_INFO}>
            <Typography variant="h5" gutterBottom className="dashboard-title" sx={SX_TITLE_MB}>
              {t('menu.dashboard')}
            </Typography>

            {tArray('dashboard.chatbox.welcomeParagraphs').map((paragraph, index) => (
              <Typography
                key={index}
                component="p"
                variant="body1"
                sx={{
                  textAlign: 'justify',
                  mt: index === 0 ? 2.5 : 2,
                }}
              >
                {renderWelcomeParagraph(paragraph)}
              </Typography>
            ))}
          </Box>

          <Box sx={SX_CHAT_WIDGET}>
        <section className={`chatbot_AIDACHATBOX position-${positionChatbox}`}>
          <header>
            <h2>{t('dashboard.chatbox.title')}</h2>
            <button 
              className="clear-chat-history_AIDACHATBOX"
              onClick={clearChatHistory}
              title={t('dashboard.chatbox.clearChatHistory')}
            >
                <svg xmlns="http://www.w3.org/2000/svg" fill="#ffffff" width="20px" className="img4_AIDACHATBOX" version="1.1" id="Capa_1" viewBox="0 0 612.002 612.002" xmlSpace="preserve">
                  <g id="SVGRepo_bgCarrier" stroke-width="0"/>
                  <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"/>
                  <g id="SVGRepo_iconCarrier"> <g> <path d="M594.464,534.414H344.219L606.866,271.77c6.848-6.851,6.848-17.953,0-24.8L407.547,47.65 c-3.291-3.288-7.749-5.135-12.401-5.135c-4.65,0-9.11,1.847-12.398,5.135L5.138,425.262c-6.851,6.848-6.851,17.95,0,24.8 l114.29,114.287c3.288,3.291,7.749,5.138,12.398,5.138h462.638c9.684,0,17.536-7.852,17.536-17.536 C612,542.265,604.148,534.414,594.464,534.414z M395.145,84.851L569.664,259.37L363.27,465.763L188.753,291.245L395.145,84.851z M294.618,534.414H139.09L42.336,437.66l121.617-121.617l174.519,174.519L294.618,534.414z"/> </g> </g>
                </svg>
            </button>
          </header>
          
          <ul className='chatbox_AIDACHATBOX' ref={chatboxRef}>
            {chatMessages.map((message) => (
              <li 
                key={message.id} 
                className={`chat_AIDACHATBOX ${message.sender === 'user' ? 'outgoing_AIDACHATBOX' : 'incoming_AIDACHATBOX'}`}
              >
                <p
                  dir="auto"
                  className={message.sender === 'user' ? 'user-text_AIDACHATBOX' : 'bot-text_AIDACHATBOX'}
                  onClick={message.sender === 'ai' ? handleMarkdownButtonClick : undefined}
                  {...(message.sender === 'ai'
                    ? { dangerouslySetInnerHTML: { __html: parseMarkdown(message.content) } }
                    : {})}
                >
                  {message.sender === 'user' ? toPersianDigits(message.content) : null}
                </p>
                {message.sender === 'ai' && (
                  <img 
                    src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAP4AAAE5AgMAAADNYnV6AAADKGlUWHRYTUw6Y29tLmFkb2JlLnhtcAAAAAAAPD94cGFja2V0IGJlZ2luPSLvu78i iglkPSJXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQiPz4gPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iQWRvYmUgWE1QIENvcmUgNy4yLWMwMDAgNzkuMWI2NWE3OWI0LCAyMDIyLzA2LzEzLTIyOjAxOjAxICAgICAgICAiPiA8cmRmOlJERiB4bWxuczpypmY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiPiA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtbG5zOnhtcE1NPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvbW0vIiB4bWxuczpzdFJlZj0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL3NUeXBlL1Jlc291cmNlUmVmIyIgeG1wOkNyZWF0b3JUb29sPSJBZG9iZSBQaG90b3Nob3AgMjMuNSAoTWFjaW50b3NoKSIgeG1wTU06SW5zdGFuY2VJRD0ieG1wLmlpZDpEM0FDRkEwNDRGNkMxMUYwQjRGM0ZGQzZGQUQwMjc1NCIgeG1wTU06RG9jdW1lbnRJRD0ieG1wLmRpZDozMjM3RDgwODRGNkYxMUYwQjRGM0ZGQzZGQUQwMjc1NCI+IDx4bXBNTTpEZXJpdmVkRnJvbSBz
                dFJlZjppbnN0YW5jZUlEPSJ4bXAuaWlkOkQzQUNGQTAyNEY2QzExRjBCNEYzRkZDNkZBRDAyNzU0IiBzdFJlZjpkb2N1bWVudElEPSJ4bXAuZGlkOkQzQUNGQTAzNEY2QzExRjBCNEYzRkZDNkZBRDAyNzU0Ii8+IDwvcmRmOkRlc2NyaXB0aW9uPiA8L3JkZjpSREY+IDwveDp4bXBtZXRhPiA8P3hwYWNrZXQgZW5kPSJyIj8+wSK1rgAAABl0RVh0U29mdHdhcmUAQWRvYmUgSW1hZ2VSZWFkeXHJZTwAAAAJUExURSMfICEpKQqvn8uancoAAAADdFJOU/AD2OyXvisAAA1SSURBVHja1Zy9jiS3Ecf/tdgSoI3mgKEBOXLig7VPUQbkvA6oEixFigR4n2IewYkE30UTHfb4lA740c1usrtnVoKtSW53tvvXxWKxvsg++M7HHt+FjT8rdu4XAKD7AQIAQLgXoOn+DRF2AMAjABLcCVCQAY/w8Ri2AYArHglB+C6AIrieH0nYcBdA4C5nJiW/DwC4izEMLuEOgIHcYSSOoHwHQMHuZCRBgtEdADmxGxlpUB4qYQsACW7kpGw0VMIGwEiCGzsZG/nIEjYAynBXdjJ2uN4OEIa7Bicjh4+0uAUIVAESRlrcAMDI052/AUDDaBrGACNjd3FHUqHeDOAMkAzgGwHKVQJxuwugRQJxI7ebAUHZHb8RwGlkSWOAzADhLoC3AP+jA/DHlMDeKkFdTP8XErwR4HcC6G2AkAG4G+A0dyg3AzQ4sldODuVtAL4LICkyOdz4DpfGLqEA9D6AZgDdFRdSKCiAcBcgMSgF1ztiY44GZOTitwOc3JHT/Xfwu8I7Zp/bAc3t45JhBJhu/45OW4QBIN1PKUfToKwjQh+gAHAiN3IYSxB2A
                cBHAQaA3eCODAgO127t1QUAcHdHcAliLA43uAtwDFAuFHYJcmIYuZK7S2cQHYCVBym5sp6YtKTKHRE6AClDNbiR4kzCuWyztQhrwOwisENxYoRy2bqAXANm1wgcZ5we4UIr+hAwu8TA8giArE7gSgTd/EJwIgA83WZLW1gBmsrEAAgwF1xoG7B4Qq7+Z18ub1j9TiujTJ9/1G/CJmBRW7U+pTeGJaDhL31KNqYtgKGjALx7Nye0Y1gAZipIj3+nOOU5KC6lHeUCMP3RkDyIgMskWHIprZ4XgCqeZQdkAOuMsFJCC5j+VhyYAuRSCAr2RYhZAGi6P12FR2D2q2CphBZQAuj
                skWcI1wGl1dikGi1A6lizlCCFUlJJKGbUKKEFoAygShIE6fpsiYbQWkIDyGgtIURBLsgeIlsBuFVCC6DGqToQHFKcyGQFOgKkP1SnCrAbSbCiQIQ0hrkWWwcUSvOnhgclCcWbp6EJN7lCA8BcgDSVysJVKYKQxiBhA5Av1jRkBFCdFgOSpgcAo9yAm+435JuKYbM7moxtDlCuAmieNKW0ksrvgLs0ae8cIFycqpVsQljSwyV9o2kORoBQPF7NRhCKM5T0L8i9SdnmAJS4VBMBhQGPyOYBSiI0WecMYGWdtGuptmSTHsA5De8DpMkjDEEAKoFJAXZFyhs6AE1TNK0ll5RrVttSwB3BMMv9ZwAJaQR1LRlYASDMrJtc2s5aC1Aqfi9PuiTfXAcFhNRg7QGQFwrqADiHpkmtBkoNVnQA5I4w5SAGuEJSQNLipARBWNllDTDKyaBPUQQMIUjyy9mlkdK8QzoBlF1pEgAgN5wJJOnH7JgV59ze6wCEy+8GwF0gTKSUEt8UGw0sYdYhnQDJQOvCT+rAmWBQACkD50SV0AO4Y1qMCMmOlOHJHku8MpxIgq0B8JIS5/sNpKQsLlRWs6SBkU6d7gmQqgOeopAgCEuQkDN1zstEAWVfAYxc2RDq/QoyhFQrsJdqIYXa82RJE4BdgtIshsE1t1Oliu8KdsGZ1gBllyBcnpTtyMiVc8iTPAhXMFVTnADB4QhWxg9yQ9DkQEt0TJHJQJMpVoAEJ0N6TFrELlQ8cLLvHJnIQegA3EjJitXmZYXgjpoAp8gEF0i15QqAGwsLgpesVFKpNXPWybEgaA9ArowzuPhvd5RSK9R4JQjuwopp06UAjFzPUEwrXxFSJpEC1pQiKVkfICcC+dz3pFzGaKpUJEVXoC6GCmAXeUTQWXpUUh5MxZKl6Arw
                EqDskBPNHBLVzF7CTARylyAdQHBAeOaQQq2/lKdSxhBcWcBGLUCCA1N9qJjlgzZPPMBupD2AAVSemRK1Wpvk1RlyQWBQkC8BbgCXkgvJL3BTBICKOLABICjN07FF8MgigF3OawBcgfyonGnOWh711jSGHoBcUbZWi/cKi2pTsqdx5T6AiuOgeb45SZ+VAjdCdUklkJArWKkkAa0ARfokggSjqTE1ASTt7Vp1qevKKE2SskNWAHZBQKhJ6aJjk6UXSlocABz1yat2i0zGZOSCLsBoGgB6G7h5atEBKLuQllzGOi2nIkLa/+kDhMtzOv2iUnRy9mlLQHCwBEGT6C5E4KxGZesDcquj2/GaxRU3GgE0RT+MuqeUTQreBZwpB3cebsWnuJIAtAY8JkOijSZpGoME6UlwSun81l56rjqVBcVWGwB3LWAxCIUrS068KkCCQxC6HcOFHg3BSCAoRVBBQ9A6gf4gggs7BHi6VqW6A3hIrYqwDTDAlRwK+iG+JqV5Pa7Ctj2AXDkaXE6glxgvSSXuwMMrgCB7B3NyEREE4BhjTCWQK55+iZe2abclgpICX316ifFCrnAXxBivwBEB3AEjA76KMcZXuMINz/FzfAUdEcBdwTgDzzHG+AVB4YoEw7wcXcB0ZuECCcA3McYYL0Hh8pQB1HThVm09mtGzBPHKCs8/z25Z7QbIrJ/oDpDgfYwxxldWOGIGNJGsbVfRPFQqIPmmV1LYQwG0w24A6z5puimSwp5ekj6wZ8fN9kMGQKFPsQBwEGDAwwzwPgP4OEEq4KLQ5wKwwwSdAyQDrpyrRV3tDK2/GQBc05pcdJK9883TDBAnwOxZWwAFRoBFL7oFPADV/XQArzRqhhfA0wUoIj4PAbN+/PLzfKk9ctkCyAgQL1WuOwHTRHYBDzuAh9eHS
                y3R4hoQN2Y9A55WgC8XhXw+CHi6Pg0keHkj4GOR4JdtwPPleVoLX6blLL+Wn8NhQPFiSYI/5yFcwnLttYCI1wnweSbBx6LQv44WbwcQY4zxc4ykkD9lmPAIoBnwl/UQSCHZpX2+7AD+Nm3bWlqMLzGSQr9ZSGAbHqk84+uZBBVw/Xq5HHkFKHr+Kk6BRb/NSrzScoMrjL7QHEsS4H2RoJdgaX+7u9zzylNg6QP64aqu5ldWVIW8UjcG9bIO+XN1pAqrAPSOQcgbAf0DqvihAILC6MvCIfgwzFfAj3PAw8smoJuplVsuQeHVrg/nB9MtF1c4fr4VMK2l1DKsuMMA+jQHyM8vtwKm1eyKyayufDRHeppsT+Hyzad7Af9JGxbvb5WgphfXBJjW5vievzeA8sif2BVuW6tpuRmb7Oh5mjeF28OPu4B2wx3ftgD6fs+W22rOigSfkUvhn/cAWADeTzco3PH9ji0v6g/Fp2nMCncJL5sAA5oTXVIW4/XrBKhO+sKDAXCzW16KhXjhDOBNgADtfj3wr49VYoW7UQH0/XJtE9ZE+YdP1QemyvVlCEjnmNqzJUQfP9bLE6CUDOg9nxZnS5Tpl4+58C2dpaeUL1zRjwuyOCKAX39+ifGnqQGheQzX9TzS+gSPBODXGK9le3eyzS/XdRiiVWuxHpeb91AE8VOM8XLqARZtDUMO99Q0bB9ijBecV9OAdWuRXE9TBlZ7KIMDeMgt1mZ7W4Pjsd2k0ZyRoGcHi2/ao4rTYSuAOocQZRXfEabdyvkWyQcK7utzlLJqDMH7gEFG8yF0vCM2TgPRbkzgTYDvhngJ7aldXet4JzXYASjvVf6bR2lGeWH7d+PNY2U7KuAdgO+kKeloyRZAeE8F7fsk67N5tKeCrRNRu0oQ9taSe8cLw44K9gBCOy
                pwbAM23/Ki4ie3Lt8YQ9p92APIRm88+MKOeoCNc+C0Xi69q8ftffalHQ2O2o4SlbBe8T3AqD1ctiz3B9zvycmRE5K18ztO9ZQOqLxXLSLtYCyXax/Q2acQUArIO8dMp4AUlpnKP+tB7CNWgzYmAgg/IV5p96ht28sOTab1FF9pNcW6kR+nc+8fTrmVgEUDfnfxLs/IJgDfsPq9aQfZQ4yvuMl9OPw7AITaeHld28cOIO+KVwD7/xhwvQ1Q/ELekX+K8d/hFkDd+Ms7uQ/d6nr7FYxmYeBGQJ2/ejTx+fXg6wd1Z7AqMe1UdjdDhwBgPgt524CPA7QFaNoN9eMAkIRSsks5VuLHAYagwT/kbNVdT+OqdmRDbbEabnuTxrB4hwsbyhp9a4tTvLjlhah8zrcJy3LDuzwpD5jdMR3CPQbIecBs/zAMo34/uHIbY3Npd/htIpvOTM/DquEoQGkeXN5Nx/CPvo4ky4Y0bWTB3RwpeEsIG1lw922i2S8nrI4l7QLG+boc8wcb/9/DMY/UtTgbKaH3PtOo3uxWlZ1sncbSyxG3LjxOEvVIaOvXruhk+qOSx8cAP5Db9+t3677Hc0PZVwKtHADwFkD332vr67B0QtYDXNfOWzq8GzB7HWAPMJyE0QiPdTD0rYAp+9e9VHc0CXWjcBcwqj9ooKNjfSTFcBqWABrm7XwIMGqFTWNYKmkJGP6vJ/UU8TZAN4/V9a5YAsJG2ngEMOrFTZ1d2wEMM28eqPkgYMpWdgDYD2rbpS/th9VNwLClOlOubAJ4u/zqTNRBAB0EjAzRwtDWFoCRHY0fcgzgvyXAtgDyZoAfANAbAf67AvBWAB0B4PcE4NBnDLC3At4swT0f/S9sw79OxPul9AAAAABJRU5ErkJggg==" 
                    className='svg_bot_AIDACHATBOX'
                    alt="Bot"
                  />
                )}
              </li>
            ))}
          </ul>
          <div className='chat-input_AIDACHATBOX'>
            <textarea 
              placeholder={t('dashboard.chatbox.chatboxPlaceholder')} 
              required
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={sendingMessage}
            ></textarea>
            <button 
              type="button"
              className='send-btn_AIDACHATBOX'
              onClick={handleSendMessage}
              disabled={sendingMessage || !chatMessage.trim()}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="20px" className="img5_AIDACHATBOX" viewBox="0 0 24 24" fill="none" transform="matrix(-1, 0, 0, 1, 0, 0)">
                    <g id="SVGRepo_bgCarrier" stroke-width="0"/>
                    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"/>
                    <g id="SVGRepo_iconCarrier"> <path d="M9.51002 4.23001L18.07 8.51001C21.91 10.43 21.91 13.57 18.07 15.49L9.51002 19.77C3.75002 22.65 1.40002 20.29 4.28002 14.54L5.15002 12.81C5.37002 12.37 5.37002 11.64 5.15002 11.2L4.28002 9.46001C1.40002 3.71001 3.76002 1.35001 9.51002 4.23001Z" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> <path d="M5.44 12H10.84" stroke="#292D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/> </g>
                </svg>
            </button>
          </div>
        </section>
          </Box>
        </Box>
      </Box>
    )}
    </>
  );
};

const Dashboard: React.FC = () => {
  const { steps_dashboard } = useTranslatedSteps();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const filteredSteps = useMemo(
    () =>
      steps_dashboard.filter((step) => {
        if (isMobile) {
          return (
            !step.selector.includes('dashboard-sidebar') &&
            !step.selector.includes('dashboard-subscription-info')
          );
        }
        return !step.selector.includes('dashboard-hamburger-menu');
      }),
    [steps_dashboard, isMobile],
  );

  return (
    <TourProvider steps={filteredSteps}>
      <VideoProvider videoUrl={videoUrls.dashboard} videoTitle={videoTitles.dashboard}>
        <DashboardContent />
      </VideoProvider>
    </TourProvider>
  );
};

export default Dashboard;
